/**
 * 기사편집 v2 (/adm/member/edit — 신버전)
 * ($j.Pages.Admin.MemberEdit / body data-page-name="MemberEdit")
 *
 * 구조: 4단 배치(기본정보 / 소속·장비·회비·정보 / 사업자·자원·제재 / 상담) + 하단 액션바.
 * 화면 전체를 탭으로 나누지는 않는다 — CS팀 근육기억 보존을 위해 v1 배치로 되돌렸다(2026-07).
 * 예외는 열2 의 '자원' 탭 하나뿐이다(발주개수·-6차·수주포인트·포인트) — 넷 다 소유자 귀속
 * 자원이고 한 번에 하나만 보므로, 세로로 쌓아 아래 블록을 밀어내는 대신 겹쳐 둔다.
 * 초기 데이터는 셸이 전역 `ME` 로 넘긴다 (edit-v2.blade.php).
 *
 * v1 화면(edit.blade.php + a-member-edit.js/css)은 제거됐다(2026-08). 거기 있던 전역함수 13개는
 * MemberEdit/Actions.js 로 옮겼고, 공용(common.js·$j.F.Admin.Util) 전역함수는 그대로 쓴다.
 *
 * ⚠️ `window.MEMBER` 브릿지
 *    공용 모듈 `$j.F.Admin.Counsel` 이 전역 MEMBER['num'] 을 직접 읽는다(DOM·전역 계약).
 *    Counsel 을 v2 에서 재사용하려면 이 전역이 있어야 한다. 리팩터(생성자 주입)는 별건.
 *
 * 설계: .ai/domains/member-edit-v2.md
 */
import Visibility from './MemberEdit/Visibility';
import Actions    from './MemberEdit/Actions';
import Save       from './MemberEdit/Save';
import Util from '../../Common/Util';

/** 사업자번호 <input> 에 하이픈 자동부여 */
function registerCoNumberHandler(id) {
    $('#' + id).on('input', function () {
        $(this).val($j.Common.Util.coNumber($(this).val()));
    });
}

export default class MemberEdit {
    initialize() {
        const d = JSON.parse(document.getElementById('page-data')?.textContent || '{}');
        this.editMode = d.editMode || 'new';
        this.mb       = d.mb   || {};
        this.meta     = d.meta || {};
        this.ref      = d.ref  || {};
        this.summary  = d.summary || {};
        this.banks    = d.banks || [];   // 은행 선택지 (g5_banks)
        this.bankLogoSprite = d.bankLogoSprite || '';
        this.car      = {};
        this.introText = this.ref.introText || '';   // 소개기사 표시명 (drawIntro 가 쓴다)

        this.isOwner = (this.meta.permit || '소유자') === '소유자';

        // 공용 Counsel 모듈이 읽는 전역 (위 주석 참고)
        window.MEMBER = this.mb;
        // F/Admin/Member/Sosok.js 가 선택 결과를 부모창의 이 전역으로 돌려준다
        window.updateSosoks = (code) => this.addSosok(code);
        // F/KakaoMap 이 주소 선택 결과를 부모창의 이 전역으로 돌려준다
        window.callbackAddress = (arr) => this.applyAddress(arr);

        // 폼은 ajax 로만 저장한다 — 입력칸 엔터가 페이지를 다시 싣지 않게
        $('#meForm').on('submit', (e) => e.preventDefault());

        this.act  = new Actions(this);
        this.save = new Save(this);

        this._normalize();
        this._bindFields();
        this._bindActions();
        this._initSosokSelects();
        this._initDatePickers();
        this._initTabs();
        this._initPortalPane();
        this._guardUnload();
        this.drawIntro();

        Visibility.applyHint(this.meta.driverType);

        if (this.editMode === 'edit') {
            this.loadCar();
            $j.F.Admin.Counsel.initCounsel();
            // 제재현황(열3 단독 블록) — 첫 렌더는 셸이 이미 받아온 값으로 하고,
            // 부과·해제 뒤에는 서버 응답으로 다시 그린다 (.ai/domains/penalty.md)
            $j.F.Admin.Member.Penalty.init(this.mb.num, this.summary.sanctions || {});
        }
        if (Util.int(this.mb.mem_status) === 99) this._lock();
        this._warnNotApproved();
        // data-tip 툴팁은 $j.Ui.Tooltip 이 생성자에서 document 위임으로 잡으므로 별도 초기화가 없다
    }

    destroy() {
        $(document).off('.me');   // 폼 밖(모달)까지 닿아야 해서 document 에 걸어둔 위임들
        if (this._onUnload) window.removeEventListener('beforeunload', this._onUnload);
        $j.F.Admin.Member.Penalty.destroy();
        delete window.MEMBER;
        delete window.updateSosoks;
        delete window.callbackAddress;
    }

    // ───────────────────────────────────────────────── 초기화

    /** DB 의 0000-00-00 은 빈값으로 (v1 initData 와 동일) */
    _normalize() {
        ['co_birth', 'mem_birthday'].forEach((k) => {
            if (this.mb[k] === '0000-00-00') {
                this.mb[k] = '';
                // 기준값(defaultValue)도 같이 비운다 — 안 그러면 사업자정보가 열자마자 '변경됨'으로 잡혀
                // 창 닫기 경고가 뜬다 (Actions.companyDirty)
                $(`#meForm [name="${k}"]`).each((i, el) => { el.value = el.defaultValue = ''; });
            }
        });
    }

    _bindFields() {
        // 전화·사업자번호 자동 하이픈 (common.js 공용)
        ['me_mem_phone', 'me_mem_phone2', 'me_co_phone', 'me_user_fax'].forEach((id) => {
            if ($(`#${id}`).length) $j.Common.Form.registerPhoneHandler(id);
        });
        if ($('#me_co_number').length) registerCoNumberHandler('me_co_number');

        // 숫자만
        $('.js-num').on('keyup', (e) => {
            e.target.value = e.target.value.replace(/[^0-9.]/g, '');
        });

        // 중복검사
        $('#me_mem_id').on('blur',    () => this.act.checkId());
        $('#me_mem_phone').on('blur', () => this.act.checkPhone());
        // 사업자번호 — 하이픈을 정리하고 그 자리에서 중복검사한다(하단 '중복체크' 버튼을 대신한다).
        // 빈칸은 그냥 지나간다: 사업자정보는 필수가 아니라 안 채운 것도 정상이고, 탭으로 스치기만
        // 해도 '올바르지 않습니다'가 뜨면 소음이 된다. 검사할 값이 있을 때만 말한다.
        $('#me_co_number').on('blur', (e) => {
            const $el = $(e.currentTarget);
            $el.val(Util.coNumber($el.val()));
            if (!$el.val()) return;
            if ($el.val().length !== 12) { $j.F.Admin.Util.tsalert('error', '사업자번호가 올바르지 않습니다.'); return; }
            this.act.checkCoNumber();
        });

        // 타입/권한 변경.
        //
        // 블록 마크업은 서버가 조합에 맞춰 렌더한 것이라, 새 조합에서 새로 생겨야 할 블록은
        // 아직 DOM 에 없다(예: 발주자 → 수발주자로 바꿔도 '보유장비' 블록이 없다).
        // v1 처럼 CSS 로만 토글하면 안 쓰는 필드가 DOM 에 남아 그대로 POST 되고, 그게 바로
        // 소속자 편집이 조용히 버려졌던 원인이다. 그래서 즉석에서 만들지 않고,
        // 구성이 어긋날 때만 "저장 후 새로 구성된다"고 알린다.
        $('#me_mem_driver_type, #me_mem_permit').on('change', () => {
            this.meta.driverType = $('#me_mem_driver_type').val() ?? this.meta.driverType;
            this.meta.permit     = $('#me_mem_permit').val()      ?? this.meta.permit;

            Visibility.applyHint(this.meta.driverType);
            if (!Visibility.matchesDom(this.meta.driverType, this.meta.permit)) {
                $j.F.Admin.Util.tsalert('info', '저장하면 이 타입·권한에 맞게 화면이 새로 구성됩니다.');
            }
        });

        // 수주승인 ↔ B/C 그룹 자동연동은 **없다** (2026-08 해제).
        //
        // 승인=B(012002)·미승인=C(012003) 이라는 관행이 있어 v1 때부터 수주승인을 뒤집으면 지사가
        // 따라갔고, 잠깐은 그 반대(지사 B↔C → 수주승인)까지 걸었다. 둘 다 뗐다 — 지사는 수주승인
        // 말고도 여러 이유로 옮기고, 수주승인도 지사와 상관없이 켜고 끈다. 한쪽을 건드릴 때마다
        // 다른 쪽이 조용히 바뀌어, 멀쩡한 회원이 오더를 못 받거나 승인 안 된 회원이 승인돼 버렸다.
        // 두 값은 이제 CS 가 각각 직접 정한다.

        // ── 아래 둘은 document 위임이다 ──
        // 지급 버튼(.js-give-point)은 화면(자원 탭)에도 있고 앞으로 모달에서 다시 쓸 수도 있어
        // 폼 밖까지 닿는 document 위임으로 둔다. `[data-clear]` 도 같은 이유(소유자코드 해제).
        $(document).on('click.me', '[data-clear]', (e) => {
            const f = $(e.currentTarget).data('clear');
            $(`#me_${f}`).val('');
            $(`input[name=${f}]`).val('');
        });

        // 비밀번호를 직접 치면 [리셋] 표시를 푼다 — 관리자가 정해준 비밀번호는 변경을 강제하지 않는다
        $('#me_mem_pwd').on('input.me', () => $('#me_pwd_reset').val(''));

        $(document).on('click.me', '.js-give-point', (e) => {
            const $b = $(e.currentTarget);
            this.act.givePoint($b.data('point'), $b.data('type'), $b.data('to'), $b.data('rel'), $b.data('label'));
        });

        // 소개기사 — 지정(검색 모달) / 변경(작은 모달). drawIntro 가 그린 버튼들이라 위임으로 건다.
        $('#meIntroBox')
            .on('click', '.js-intro-pick', () => this.act.findIntroducer())
            .on('click', '.js-intro-edit', () => this.act.introEdit());
        // 팝업 안의 회원번호 링크 — 팝업은 body 에 붙으므로 문서에 위임
        $(document).on('click.me', '.js-intro-open', (e) => {
            e.preventDefault();
            $j.F.Admin.Util.memberEditSub($(e.currentTarget).data('mb'));
        });

        // 소유자 검색
        $('#me_mem_ownercode').on('click', () => this.act.findOwner());

        // 차고지 주소
        $('#me_car_parkaddress').on('click', () => this.act.findParkAddress());

        // 열1 '위치동의'·'마케팅동의' 라벨 → 약관 동의 내역 모달 (둘 다 같은 이력을 본다)
        $('.js-terms-log').on('click', () => this.act.termsLog());

        // 계좌
        $('#meBank1').on('click', () => this.act.bankEdit(1));
        $('#meBank2').on('click', () => this.act.bankEdit(2));
        $('#meBank3').on('click', () => this.act.assignVirtualAccount());
        $('.me-copy').on('click', (e) => this.act.bankCopy($(e.currentTarget).data('copy')));
    }

    _bindActions() {
        const a = this.act;
        const map = {
            meIdChange:  () => a.idChange(),
            mePwdReset:  () => a.pwdReset(),
            meSosokEdit: () => a.editSosok(),   // 열1 대표소속 옆 [+]
            meSosokAdd:  () => a.editSosok(),   // 열2 추가소속 블록 헤더 [+] (블록이 보일 때만 존재)
            meCarAdd:    () => a.carAdd(),
            meCarBox:    () => a.carAdd(),
            meCarList:   () => a.carList(),
            meCarList2:  () => a.carList(),   // 하단 액션바의 같은 버튼 (열2 아이콘과 한 동작)
            meBaljooSet: () => a.baljooSet(),
            meFeeSet:    () => a.feeSet(),
            meFeeLog:    () => a.feeLog(),
            meCashLog:   () => a.cashLog(),
            meCashInout: () => a.cashInout(),
            meCashRecalc:() => a.cashRecalc(),
            mePointRecalc:() => a.pointRecalc(),
            mePayatReg:  () => a.payatReg(),
            meCmsApply:  () => a.cmsForm(),
            mePushTest:  () => a.pushTest(),
            meMemberLogs:() => a.memberLogs(),
            meLoginLogs: () => a.loginLogs(),
            meAppDiag:   () => a.appDiag(),
            meDocs:      () => a.docs(),
            meLeave:     () => a.leave(),
            meRecover:   () => a.recover(),
            meTransfer:  () => a.transfer(),
            meAddChild:  () => a.addChild(),
            meSave:      () => this.save.submit(),

            // ── 자원 탭의 현황 → 최근 내역 모달 (설계 §6) ──
            meMsix:      () => a.msixList(),     // -6차
            meOdrLog:    () => a.odrcntLog(),    // 발주개수
            mePointLog:  () => a.pointLog(),     // 수주포인트
        };
        Object.entries(map).forEach(([id, fn]) => {
            $(`#${id}`).on('click', (e) => { e.preventDefault(); fn(); });
        });

        // 사업자정보 — 요약줄과 제목줄 연필 둘 다 같은 모달을 연다
        $('.me-co-open').on('click', (e) => { e.preventDefault(); a.companyEdit(); });

        // ↓ 접는 묶음(소속자▾ · 로그▾). 한 번에 하나만 열고, 바깥을 누르거나 Esc 로 닫는다.
        //   안의 항목은 위 map 이 이미 잡고 있으므로 여기서는 **여닫기만** 한다.
        $('.me-drop__btn').on('click', (e) => {
            e.preventDefault();
            e.stopPropagation();                      // 아래 document 핸들러가 방금 연 것을 도로 닫는다
            const $d = $(e.currentTarget).closest('.me-drop');
            const on = $d.hasClass('is-open');
            $('.me-drop').removeClass('is-open');
            $d.toggleClass('is-open', !on);
        });
        $(document).on('click.medrop', () => $('.me-drop').removeClass('is-open'));
        $(document).on('keydown.medrop', (e) => {
            if (e.key === 'Escape') $('.me-drop').removeClass('is-open');
        });
        // 항목을 고르면 닫는다 — 열린 채 모달이 뜨면 그 위에 메뉴가 남는다
        $('.me-drop__item').on('click', () => $('.me-drop').removeClass('is-open'));
    }

    /** 본사/지사/센터 3단 select (common.js 공용 핸들러 재사용) */
    _initSosokSelects() {
        if (!this.isOwner) return;

        $j.Common.Form.registerBonsaHandler('me_bonsa', 'me_gisa', 'me_center', (sosok) => {
            // 삼성 계열은 사원코드를 요구한다
            if (Util.isSosokSamsung(sosok)) $('#meSamsungRow').removeClass('me-off');
            else $('#meSamsungRow').addClass('me-off').find('input').val('');

            // 장비불러 소속이 아니면 '발주자'는 성립하지 않는다 → 업체발주자로 승격
            if ($('#me_mem_driver_type').val() === '발주자' && !Util.isSosokJbbuller(sosok)) {
                $('#me_mem_driver_type').val('업체발주자').trigger('change');
                // 실패가 아니라 '시스템이 대신 바꿨다'는 통지다 — 경고색을 쓰면 경고가 흔해진다
                $j.F.Admin.Util.tsalert('info', '기사타입 : 발주자 → 업체발주자');
            }
        });
        $j.Common.Form.registerGisaHandler('me_gisa', 'me_center');
        $j.Common.Form.updateBonsaSelectBox(this.mb.sosok, 'me_bonsa', 'me_gisa', 'me_center');
        if (this.mb.sosok && Util.isSosokSamsung(this.mb.sosok)) $('#meSamsungRow').removeClass('me-off');

        $('#me_bonsa, #me_gisa, #me_center').on('change', () => this.updateSosokCode());
        this.drawSosoks();
    }

    /**
     * 탭 블록 — 지금 화면에는 쓰는 곳이 없다. 마크업은 서버가 다 그려둔다.
     * (계좌·보유자원·정보│제재현황 탭은 없앴다 — 같이 보는 일이 잦아 다 펼쳐 둔다.)
     * 여러 벌이 생겨도 블록 단위로 스코프를 잡는다 (`.me-tabs` 안에서만 토글).
     */
    /**
     * 창 닫기 경고 — 사업자정보를 고쳐 놓고 [저장]하지 않은 채 창(팝업)을 닫거나 떠나려 하면 묻는다.
     * 사업자정보는 모달에서 [확인]까지 해도 하단 [저장]을 눌러야 반영되는데, 그 한 단계를 빠뜨리기 쉽다.
     * 문구는 브라우저가 정한다 (beforeunload 는 사이트 문구를 보여주지 않는다).
     */
    _guardUnload() {
        this._onUnload = (e) => {
            if (!this.act.companyDirty()) return;
            e.preventDefault();
            e.returnValue = '';   // 크롬 구버전 호환
        };
        window.addEventListener('beforeunload', this._onUnload);
    }

    _initTabs() {
        $('.me-tabs').each((i, el) => {
            const $tabs = $(el);
            $tabs.on('click', '.me-tab', (e) => {
                const key = $(e.currentTarget).data('tab');
                $tabs.find('.me-tab').removeClass('is-on');
                $(e.currentTarget).addClass('is-on');
                $tabs.find('.me-tabs__pane').removeClass('is-on')
                     .filter(`[data-pane="${key}"]`).addClass('is-on');
            });
        });
    }

    /**
     * 하단 액션바의 '모바일화면 닫기/열기' — 우측 회원 모바일화면(.me-shell__mobile) 여닫기.
     *
     * 예전엔 이 버튼이 부모창(포털 셸)의 `parent.jbPortal` 을 불렀다. 지금은 우측창이 같은
     * 문서 안에 있어 형제 요소를 직접 토글한다 (edit-v2.blade.php 상단 주석).
     * 우측창이 없는 화면(신규등록·소속자추가, iframe 안, 폰)에서는 붙일 대상이 없으므로
     * 버튼을 숨긴 채로 둔다 — 서버가 `.me-off` 로 내려보낸다.
     *
     * 초기 접힘은 블레이드의 선행 스크립트가 이미 잡아 뒀다. 여기서는 그 상태를 읽어
     * 지연 로드(_attachPortalPane)와 라벨을 맞추고, 이후의 토글을 맡는다.
     */
    /**
     * 우측 모바일화면 지연 로드 + 세션 확인. 접힘·제거는 블레이드 선행 스크립트가 그리기 전에 정했다.
     *
     * src 가 비어 있다 — 접어 쓰는 직원에게 이 프레임은 안 보이는데도 대리로그인 + 모바일 번들 + ajax 까지
     * 20요청·185ms 를 먹었다. 펼침일 때만 주소를 넣는다.
     */
    _attachPortalPane($pane, $shell) {
        const pane = $pane[0];
        const f = pane.querySelector('iframe');

        /* 펼침이면 **이 페이지의 첫 ajax 가 끝난 뒤** 붙인다. 대리로그인은 세션ID 를 새로 내는데, 동시에 나간
           /adm ajax 가 나중에 돌아오면 옛 세션ID 로 쿠키를 되굽고 옛 내용을 되써서 우측창이 로그인 풀림·다른
           회원이 된다. load 이후에 보고, 3초가 넘으면 기다리지 않고 붙인다. */
        if (!$shell.hasClass('is-collapsed')) {
            const start = () => {
                let waited = 0;
                (function attach() {
                    const busy = $.active > 0;
                    if (busy && waited < 3000) { waited += 100; setTimeout(attach, 100); return; }
                    if (!f.src) f.src = f.dataset.src;   // 그새 [모바일화면 열기] 토글이 붙였으면 그대로
                })();
            };
            if (document.readyState === 'complete') start();
            else window.addEventListener('load', start);
        }

        /* 모바일 세션은 브라우저에 하나라, 다른 창(기사편집·출금확인·불러톡 앱화면 등)에서 대리로그인하면
           이 창의 우측창도 그 회원으로 바뀐다 — 화면은 이 회원인데 누르면 다른 회원 데이터가 나온다.
           이 창으로 돌아올 때 세션 회원을 물어 **다를 때만** 다시 대리로그인한다(같으면 보던 화면 유지).
           focus 이벤트 대신 hasFocus 를 본다 — 창 안의 iframe 을 오갈 때는 묻지 않는다 (account/review 와 같은 방식). */
        const paneNo = parseInt(pane.dataset.paneNo, 10);
        let had = document.hasFocus();
        setInterval(() => {
            const has = document.hasFocus();
            const back = has && !had;
            had = has;
            const fr = pane.querySelector('iframe');
            if (!back || !fr || !fr.src || !pane.isConnected) return;   // 접혀서 아직 안 붙었으면 펼칠 때 붙는다
            fetch('/m/ajax/member/info', { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
                .then((r) => r.json())
                .then((d) => { if (parseInt(d && d.num, 10) !== paneNo) fr.src = fr.dataset.src; })
                .catch(() => { fr.src = fr.dataset.src; });   // 세션 없음(로그인 화면 응답) 등
        }, 500);
    }

    _initPortalPane() {
        const $btn   = $('#mePortalPane');
        const $shell = $('#meShell');
        const $pane  = $('#mePane');

        // 팝업 폭 — 우측창을 접으면 편집 본문(1250px)만 남으므로 창도 같이 줄인다.
        // $j.F.Admin.Util.portalWidth() 가 처음 띄울 때 쓰는 값과 **같아야 한다**.
        // 배율은 곱하지 않는다 — 창은 그대로 두고 상담열이 줄며 확대분을 흡수하는 설계다
        // (pages/_member-edit-v2.scss $me-counsel). 여는 쪽($j.F.Admin.Util.memberPortal)도
        // `scale:false` 라 두 값이 그대로 맞물린다.
        const W_OPEN = 1700, W_CLOSED = 1300;

        // 우측창이 아예 없는 회원(탈퇴·탈퇴신청 — edit-v2.blade.php 가 미렌더)은 목록에서
        // portalWidth() 취향대로 1700 으로 열려 오른쪽이 통째로 빈다. 여기서 접힘 폭으로 줄인다.
        // 취향 키는 건드리지 않는다 — 다음에 정상 회원을 열 때는 그대로 펼쳐져야 한다.
        if (!$pane.length) {
            if (window.top === window.self && !$j.Common.Util.isMobile()) {
                try { window.resizeTo(W_CLOSED, window.outerHeight); } catch (e) { /* 무시 */ }
            }
            return;
        }
        this._attachPortalPane($pane, $shell);
        if (!$btn.length) return;
        if ($j.Common.Util.isMobile()) return;   // 폰은 두 창을 못 넣는다 (scss 도 같은 폭에서 내린다)

        // 사이드 창인가 — 셸의 선행 스크립트가 같은 판정으로 초기 접힘을 정한다
        const isSideWindow = /[?&]pane=0(&|$)/.test(location.search);

        const KEY = G5.LS_ADM_PORTAL_PANE;       // 'adm:member:portalPane' (common.js 정본)
        // 선행 스크립트는 common.js 를 기다릴 수 없어 키를 문자열로 박아 쓴다.
        // 정본이 바뀌면 초기 접힘상태가 안 먹으니 여기서 어긋남을 잡아 알린다.
        if (KEY !== 'adm:member:portalPane') {
            console.warn('[me] 키 정본이 바뀌었다 — edit-v2 선행 스크립트의 문자열도 같이 고칠 것:', KEY);
        }

        // 라벨은 '누르면 무엇이 되는지'를 말한다 — 펼쳐져 있으면 [모바일화면 닫기]
        const label = (collapsed) => $btn.text(collapsed ? '모바일화면 열기' : '모바일화면 닫기');

        label($shell.hasClass('is-collapsed'));
        $btn.removeClass('me-off').on('click', () => {
            const collapsed = !$shell.hasClass('is-collapsed');

            // 펼치는 순간 처음 붙는다 (접힌 채로 열었으면 아직 src 가 없다).
            // 이미 붙어 있으면 손대지 않는다 — src 를 다시 넣으면 대리로그인이 새로 붙는다.
            if (!collapsed) {
                const f = $pane.find('iframe')[0];
                if (f && !f.src) f.src = f.dataset.src;
            }

            $shell.toggleClass('is-collapsed', collapsed);
            // 사이드 창(`?pane=0` — Util.memberEditSub)에서는 취향을 쓰지 않는다.
            // 잠깐 열어본 창에서 한 번 펼친 것이 목록에서 여는 기본값까지 바꾸면 안 된다.
            if (!isSideWindow) $j.Common.Store.local.set(KEY, collapsed ? 'closed' : 'open');

            // 창 크기 맞추기는 window.open 으로 띄운 팝업에서만 먹는다 — 주소를 탭에 직접
            // 붙여넣어 열면 브라우저가 무시한다. 높이는 건드리지 않는다.
            try { window.resizeTo(collapsed ? W_CLOSED : W_OPEN, window.outerHeight); } catch (e) { /* 무시 */ }

            label(collapsed);
        });
    }

    /**
     * 날짜칸에 달력을 붙인다.
     *
     * `$sel` 을 주면 그 범위만 — 나중에 만들어지는 마크업(기사/장비 관리 모달의 기사편집 폼)이
     * 같은 설정을 그대로 쓰게 하려고 열어뒀다. **여기가 설정의 정본이다**: 화면마다 옵션을
     * 따로 쓰면 어떤 칸은 오늘 버튼이 있고 어떤 칸은 없는 식으로 갈린다.
     */
    _initDatePickers($sel) {
        if ($j.Common.Util.isMobile()) return;   // 모바일은 네이티브 입력이 낫다

        ($sel || $('.js-date')).mobiscroll().datepicker({
            controls: ['calendar'],
            dateFormat: 'YYYY-MM-DD',
            locale: mobiscroll.localeKo,
            buttons: [],
            // 머리(이전·오늘·다음)는 전역 기본값을 쓴다 — Common/Mobiscroll.js
        });
    }

    /**
     * 수주 미승인 경고 — 화면 진입 시 한 번.
     *
     * 서비스상태가 '등록'인 수발주자인데 수주승인(is_soojoo)이 꺼져 있으면 오더를 받을 수 없다.
     * "정상 회원인데 왜 수주가 안 되죠?" 문의의 흔한 원인인데, 값이 열1 아래쪽 라디오 한 쌍이라
     * 스크롤해서 보기 전에는 눈에 띄지 않는다. 들어오자마자 알려준다.
     */
    _warnNotApproved() {
        if (this.meta.driverType !== '수발주자') return;
        if (Util.int(this.mb.mem_status) !== 0) return;      // 대기·탈퇴는 그 자체가 이미 사유다
        if (Util.int(this.mb.is_soojoo) === 1) return;

        // 6초는 너무 길었다 — 화면 한가운데 위를 오래 가려 편집을 방해했다(2026-08).
        // 2초 · 좌측 상단에 바짝 붙여 '지나가는 알림'으로 둔다. 토스트가 사라진 뒤의 상태는
        // 열1 '수주승인' 스위치(_col1)가 보여준다.
        $j.Ui.Toast.show('수주 미승인 — 오더를 받을 수 없습니다.',
            { type: 'warning', timeout: 2000, place: 'top-left' });
    }

    /**
     * 탈퇴회원 — 편집 잠금. 상담 패널은 계속 쓸 수 있어야 한다 (CS 가 기록을 남긴다).
     * 자원 탭 전환과 현황 조회(me-res)도 열어 둔다 — 값을 바꾸지 않고 보기만 하는 컨트롤이고,
     * 탈퇴 경위를 물어보는 CS 가 가장 자주 찾는 정보다.
     */
    _lock() {
        const $form = $('#meForm');
        // 상담 열(.me-col--counsel)은 통째로 열어 둔다 — 상담 등록·수정은 자기 ajax 로 저장하므로
        // 폼 잠금(저장 버튼 비활성)과 무관하고, 탈퇴 회원일수록 남길 기록이 많다.
        $form.find('input,select,textarea,button')
             .not('.me-col--counsel *').not('.me-tab, .me-res, .me-co-open').prop('disabled', true);
        // 복구는 잠금을 푸는 버튼이고, 모바일화면 여닫기는 '보는 방식'이라 회원 잠금과 무관하다
        $('.me-actions').find('a,button').not('#meRecover, #mePortalPane')
            .addClass('disabled').prop('disabled', true);
        $('#meSave').prop('disabled', true);
    }

    // ───────────────────────────────────────────────── 소속

    /** 3단 select 중 가장 하위 선택값을 대표소속(sosok)으로 확정 */
    updateSosokCode() {
        const code = $('#me_center').val() || $('#me_gisa').val() || $('#me_bonsa').val() || '';
        $('input[name=sosok]').val(code);
    }

    /**
     * 추가소속 목록 그리기.
     * 추가소속이 없는 회원이 대다수라 블록 자체를 접어 둔다 — 값이 생기면 펼친다.
     * (서버도 같은 규칙으로 `display:none` 을 찍어 보낸다: v2/_col2.blade.php)
     */
    drawSosoks() {
        const val = $('input[name=sosoks]').val();
        const $box = $('#meSosoksBox').empty();
        $('#meSosokBlock').toggleClass('me-off', !val);
        $('#meCol2Empty').toggle(!val);   // 빈 열 자리지킴 — 소속이 생기면 치운다
        if (!val) return;

        $.getJSON('/adm/ajax/member/sosoks', { sosoks: val }, (list) => {
            list.forEach((s) => {
                const text = [s[1], s[2], s[3]]
                    .map((p) => (Array.isArray(p) ? p.join(' / ') : p))
                    .filter(Boolean).join(' / ');
                $('<div>').text(text).append(
                    $('<button type="button">').text('×')
                        .attr('style', 'margin-left:4px;color:#dc3545;font-weight:700;border:none;background:none;cursor:pointer')
                        .on('click', () => this.removeSosok(s[0]))
                ).appendTo($box);
            });
        });
    }

    /** 추가소속 추가 (F/Admin/Member/Sosok.js 가 window.updateSosoks 로 호출) */
    addSosok(code) {
        const main = $('input[name=sosok]').val();
        let arr = (($('input[name=sosoks]').val() || '').split(',')).filter(Boolean);

        arr = arr.filter((c) => c !== main);                     // 대표소속은 추가소속에 두지 않는다
        if (code && code !== main && !arr.includes(code)) arr.push(code);

        $('input[name=sosoks]').val(arr.join(','));
        this.drawSosoks();
    }

    removeSosok(code) {
        const arr = (($('input[name=sosoks]').val() || '').split(',')).filter((c) => c && c !== code);
        $('input[name=sosoks]').val(arr.join(','));
        this.drawSosoks();
    }

    // ───────────────────────────────────────────────── 소개기사

    /**
     * 소개기사 영역 그리기 — 상태가 셋이라 마크업을 한 곳(여기)에서만 만든다.
     *
     *   미설정   : [ + 소개기사 ]              ← 누르면 회원검색 모달
     *   설정됨   : [ 이름 ]                 ← 누르면 팝업(Actions.introEdit): 점수 지급 / 수정 / 해제
     *   지급완료 : ✓ 소개
     *
     * 페이앳·스티커와 한 줄에 선다(_give) — 점수·대상은 툴팁과 확인창이 말하고 버튼엔 적지 않는다.
     * 지급여부(ref.points.intro)는 **처음 소개기사 기준**이라, 다른 사람으로 바꾸면 미지급으로 본다.
     */
    drawIntro() {
        const $box = $('#meIntroBox');
        if (!$box.length) return;

        const num = Util.int($('input[name=mem_introducer]').val());
        if (!num) {
            // 색은 페이앳·스티커 지급 버튼과 같은 기본색으로 — 셋 다 같은 성격(포인트 관련)이라
            // 하나만 강조색이면 더 중요한 일처럼 읽힌다.
            $box.html(`<button type="button" class="me-btn js-intro-pick"
                               data-tip-hover data-tip="소개기사 지정 — 지정하면 소개 보상 1500점을 지급할 수 있습니다"><i class="fa-solid fa-plus"></i> 소개기사</button>`);
            return;
        }

        // 이름은 버튼 안에 넣는다 — 길면 말줄임(.me-intro-give), 전체는 툴팁에.
        const name = this.introLabel(num);

        // 소개보상은 **이 회원에 대해 한 번뿐**이다. 받은 사람이 누구였든(소개기사를 바꿨더라도)
        // 이미 나갔으면 다시 못 준다 — 서버도 같은 기준으로 막는다(givePoint).
        // 지급이 끝났으면 편집 입구도 없앤다. 바꿔봐야 새 소개기사에게는 줄 수 없어서
        // 입구가 남아 있으면 "바꾸면 다시 줄 수 있나" 로 읽힌다.
        const paidTo = Util.int((this.ref.points || {}).introTo);
        if (paidTo) {
            $box.html(`
                <span class="me-paid" data-tip-hover data-tip-title="소개 1500점 지급완료"
                      data-tip="이 회원의 소개 보상은 이미 ${paidTo === num ? name : `#${paidTo}`} 에게 지급되었습니다. 소개 보상은 회원당 한 번뿐이라 소개기사를 바꿔도 다시 지급할 수 없습니다."><i class="fas fa-check"></i> <span class="me-paid__txt">소개 ${paidTo === num ? name : `#${paidTo}`}</span></span>`);
            return;
        }

        // 버튼은 하나 — 누르면 팝업(Actions.introEdit)에서 [소개점수 지급]·[소개자 수정]·[해제] 를 고른다.
        $box.html(`
            <button type="button" class="me-btn me-intro-give js-intro-edit"
                    data-tip-hover data-tip="소개자 ${name} — 누르면 소개점수 지급·소개자 수정">${name}</button>`);
    }

    /** 소개자 표시 '이름#회원번호' — introText 는 '이름 (번호)' 로 오므로 꼬리를 떼고 다시 붙인다 */
    introLabel(num) {
        const nm = String(this.introText || '').replace(/\s*\(\d+\)\s*$/, '').trim();
        return this._esc(nm ? `${nm}#${num}` : `#${num}`);
    }

    _esc(s) {
        return String(s ?? '').replace(/[&<>"']/g, (c) => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
        }[c]));
    }

    // ───────────────────────────────────────────────── 장비 · 주소

    /** 차고지 주소 선택 결과 (F/KakaoMap 콜백) */
    applyAddress(arr) {
        const gps = `${arr.lat},${arr.long}`;
        $('#me_car_parkaddress').val(arr.addr);
        $('input[name=gps]').val(gps);
        $('input[name=is_gps_update]').val(1);
        this.mb.gps = gps;
    }

    /** 장비 정보 조회 + 표시 */
    loadCar() {
        $.getJSON(`/adm/ajax/car/${this.mb.num}`, (d) => {
            this.car = { ...d };
            this.drawCar();
        });
    }

    /**
     * 보유장비 요약 — v1 과 같은 `이름 : 값` 표기 (배지는 되돌렸다, 2026-08).
     * 줄만 `.me-carline` 으로 감싼다 — 줄바꿈을 막고, 넘치면 박스가 옆으로 스크롤한다.
     * 전고는 기종과 같은 줄에 둔다 (기종을 읽을 때 같이 보는 값이고, 줄도 하나 아낀다).
     */
    drawCar() {
        const c = this.car;
        if (!c.car_type) { $('#meCarBox').empty(); return; }

        const num  = c.car_platenumber ? this._esc(c.car_platenumber) : '<b style="color:#dc3545">차량번호 미지정</b>';
        const high = c.car_height ? ` · 전고 : ${this._esc(c.car_height)}` : '';

        $('#meCarBox').html(
            `<div class="me-carline">번호 : ${num}</div>`
            + `<div class="me-carline">기종 : <b style="color:#0d6efd">[${this._esc(c.car_type)}]</b>`
            + ` ${$j.F.Car.getCarRangeStr(c)}${high}</div>`
            + `<div class="me-carline">옵션 : ${this._esc(c.car_option)}</div>`
        );
    }
}
